import React, { useEffect, useState } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Megaphone, Plus, Trash2, Eye, EyeOff, AlertTriangle, Info } from 'lucide-react';
import { useAuth } from '../AuthContext';
import useAutoRefresh from '../hooks/useAutoRefresh';

const HQ_EMAIL = 'hq@wwags.org';

const Notices = () => {
  const { user } = useAuth();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', priority: 'info' });
  const [submitting, setSubmitting] = useState(false);

  const isHqAdmin = user?.email?.toLowerCase() === HQ_EMAIL && user?.role === 'Admin';

  const fetchNotices = async (silent = false) => {
    if (!silent) setLoading(true);
    try { const r = await api.get('/notices'); setNotices(r.data); }
    catch { if (!silent) toast.error('Failed to load notices'); }
    finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { fetchNotices(); }, []);

  // Real-time auto-refresh (silent — no spinner / toast)
  useAutoRefresh(() => fetchNotices(true));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) { toast.error('Title and message are required'); return; }
    setSubmitting(true);
    try {
      await api.post('/notices', form);
      toast.success('Notice published to all users');
      setDialogOpen(false);
      setForm({ title: '', message: '', priority: 'info' });
      fetchNotices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to publish'); }
    finally { setSubmitting(false); }
  };

  const toggleActive = async (notice) => {
    try {
      await api.patch(`/notices/${notice.id}`, { is_active: !notice.is_active });
      fetchNotices();
      toast.success(notice.is_active ? 'Notice deactivated' : 'Notice re-activated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this notice permanently? (All users will lose access to it)')) return;
    try {
      await api.delete(`/notices/${id}`);
      toast.success('Notice deleted');
      fetchNotices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const priMeta = (p) => ({
    urgent: { icon: AlertTriangle, cls: 'bg-red-50 border-red-200 text-red-700', chip: 'bg-red-600 text-white' },
    important: { icon: Megaphone, cls: 'bg-amber-50 border-amber-200 text-amber-700', chip: 'bg-amber-500 text-white' },
    info: { icon: Info, cls: 'bg-blue-50 border-blue-200 text-blue-700', chip: 'bg-blue-600 text-white' },
  }[p] || { icon: Info, cls: 'bg-blue-50 border-blue-200 text-blue-700', chip: 'bg-blue-600 text-white' });

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="notices-page">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Notices & Announcements</h1>
          <p className="text-sm text-slate-600">
            {isHqAdmin
              ? 'Compose and broadcast notices to all users. They appear as a popup on every user\'s login.'
              : 'Notices broadcasted by HQ. Check here for official communications.'}
          </p>
        </div>
        {isHqAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="compose-notice-btn">
                <Plus className="w-4 h-4 mr-2" />Compose Notice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Compose Notice (from {HQ_EMAIL})</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                <div className="space-y-1">
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="notice-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">Info (Blue)</SelectItem>
                      <SelectItem value="important">Important (Amber)</SelectItem>
                      <SelectItem value="urgent">Urgent (Red)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Title *</Label>
                  <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Monthly data reconciliation deadline" required data-testid="notice-title-input" maxLength={120} />
                </div>
                <div className="space-y-1">
                  <Label>Message *</Label>
                  <Textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Full message body. Line breaks are preserved." rows={6} required data-testid="notice-message-input" maxLength={2000} />
                  <p className="text-[10px] text-slate-400">{form.message.length}/2000 characters</p>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setForm({ title: '', message: '', priority: 'info' }); }}>Cancel</Button>
                  <Button type="submit" disabled={submitting} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="publish-notice-btn">
                    {submitting ? 'Publishing...' : 'Publish to all users'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-700"></div></div>
      ) : notices.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <Megaphone className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>No notices have been published yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notices.map(n => {
            const m = priMeta(n.priority);
            const Icon = m.icon;
            return (
              <div key={n.id} className={`rounded-xl border ${m.cls} p-4 md:p-5 ${!n.is_active ? 'opacity-60' : ''}`} data-testid={`notice-item-${n.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`${m.chip} w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-4 h-4" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${m.chip}`}>{(n.priority || 'info').toUpperCase()}</span>
                      {!n.is_active && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-300 text-slate-700">INACTIVE</span>}
                      {n.acknowledged && <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Read by you</span>}
                      <span className="text-[11px] text-slate-500">by {n.created_by_email}</span>
                      <span className="text-[11px] text-slate-400">{new Date(n.created_at).toLocaleString('en-IN')}</span>
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-1">{n.title}</h3>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.message}</p>
                    {isHqAdmin && (
                      <p className="text-[11px] text-slate-500 mt-2">
                        Acknowledged by <span className="font-semibold text-slate-700">{n.ack_count || 0}</span> user{(n.ack_count || 0) === 1 ? '' : 's'}
                      </p>
                    )}
                  </div>
                  {isHqAdmin && (
                    <div className="flex flex-col gap-1">
                      <Button variant="ghost" size="sm" onClick={() => toggleActive(n)} className="h-7 px-2" title={n.is_active ? 'Deactivate' : 'Reactivate'} data-testid={`toggle-${n.id}`}>
                        {n.is_active ? <EyeOff className="w-4 h-4 text-slate-600" /> : <Eye className="w-4 h-4 text-emerald-600" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(n.id)} className="h-7 px-2 text-red-600 hover:text-red-700" title="Delete" data-testid={`delete-${n.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Notices;
